// components/LocaleConfirmationModal.tsx
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState, useEffect, useMemo } from 'react';
import CustomDropdown from './CustomDropdown';

interface CountryInfo {
  country: string;
  countryCode: string;
  ip: string;
}

interface Country {
  name: string;
  code: string;
  currency: string;
}

interface LocaleConfirmationModalProps {
  isOpen: boolean;
  detectedCountry: CountryInfo;
  onConfirm: (locale: string, currency: string, country: string, countryCode: string) => void;
}

const LocaleConfirmationModal: React.FC<LocaleConfirmationModalProps> = ({
  isOpen,
  detectedCountry,
  onConfirm,
}) => {
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCountrySelection, setShowCountrySelection] = useState(false);
  const [showResetWarning, setShowResetWarning] = useState(false);
  const [modalLanguage, setModalLanguage] = useState<'ar' | 'en'>('en');

  const { t } = useTranslation('common');
  const { locale } = useRouter();

  // Available currencies
  const availableCurrencies = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'ILS', name: 'Israeli Shekel' }
  ];

  // Country configuration with default currency
  const countryConfig: { [key: string]: { defaultCurrency: string; isEuropean?: boolean } } = {
    'SA': { defaultCurrency: 'USD', isEuropean: false }, // Saudi Arabia (GCC)
    'OM': { defaultCurrency: 'USD', isEuropean: false }, // Oman (GCC)
    'BH': { defaultCurrency: 'USD', isEuropean: false }, // Bahrain (GCC)
    'AE': { defaultCurrency: 'USD', isEuropean: false }, // UAE (GCC)
    'QA': { defaultCurrency: 'USD', isEuropean: false }, // Qatar (GCC)
    'KW': { defaultCurrency: 'USD', isEuropean: false }, // Kuwait (GCC)
    'BE': { defaultCurrency: 'EUR', isEuropean: true }, // Belgium (EU)
    'NL': { defaultCurrency: 'EUR', isEuropean: true }, // Netherlands (EU)
    'GB': { defaultCurrency: 'USD', isEuropean: true }, // UK (Europe but not EU)
    'ES': { defaultCurrency: 'EUR', isEuropean: true }, // Spain (EU)
    'IT': { defaultCurrency: 'EUR', isEuropean: true }, // Italy (EU)
    'AT': { defaultCurrency: 'EUR', isEuropean: true }, // Austria (EU)
    'FR': { defaultCurrency: 'EUR', isEuropean: true }, // France (EU)
    'US': { defaultCurrency: 'USD', isEuropean: false }, // USA
    'CA': { defaultCurrency: 'USD', isEuropean: false }, // Canada
    'IL': { defaultCurrency: 'ILS', isEuropean: false }, // Israel
  };

  // Determine language based on country
  const getLanguageForCountry = (countryCode: string): 'ar' | 'en' => {
    // Arabic-speaking countries
    const arabicCountries = ['SA', 'OM', 'BH', 'AE', 'QA', 'KW', 'IL'];
    return arabicCountries.includes(countryCode) ? 'ar' : 'en';
  };

  // Get available currencies for a country - Filter out disabled options
  const getAvailableCurrenciesForCountry = (countryCode: string) => {
    if (countryCode === 'IL') {
      // Israel can only use ILS
      return availableCurrencies.filter(currency => currency.code === 'ILS');
    } else {
      // All other countries can use USD or EUR
      return availableCurrencies.filter(currency => currency.code === 'USD' || currency.code === 'EUR');
    }
  };

  // Get default currency based on country
  const getDefaultCurrencyForCountry = (countryCode: string): string => {
    const config = countryConfig[countryCode];
    if (config) {
      return config.defaultCurrency;
    }
    return 'USD'; // Default fallback
  };

  // Manual translations
  const getTranslation = (key: string): string => {
    const translations: { [key: string]: { en: string; ar: string } } = {
      'localeModalWelcomePartOne': {
        en: 'Welcome to',
        ar: 'مرحباً بك في'
      },
      'localeModalWelcomePartTwo': {
        en: 'Basmti',
        ar: 'بصمتي'
      },
      'detectionHeading': {
        en: 'It loos like you are in',
        ar: 'نعتقد أنك في'
      },
      'notCorrect': {
        en: 'Not correct?',
        ar: 'ليس صحيحاً؟'
      },
      'change': {
        en: 'Change',
        ar: 'تغيير'
      },
      'continueShoppingOne': {
        en: 'Shop',
        ar: 'متابعة التسوق'
      },
      'continueShoppingTwo': {
        en: 'website - ',
        ar: 'متابعة التسوق'
      },
      'selectDifferentCountry': {
        en: 'Select Different Country',
        ar: 'اختيار دولة أخرى'
      },
      'countryChangeWarning': {
        en: 'Changing country will reset your cart and product selection',
        ar: 'تغيير الدولة سيعيد تعيين سلة التسوق واختيار المنتجات'
      },
      'selectCountry': {
        en: 'Select Country',
        ar: 'اختر الدولة'
      },
      'selectCurrency': {
        en: 'Select Currency',
        ar: 'اختر العملة'
      },
      'loadingCountry': {
        en: 'Loading countries...',
        ar: 'جاري تحميل الدول...'
      },
      'defaultCurrency': {
        en: 'Default Currency',
        ar: 'العملة الافتراضية'
      },
      'defaultLanguage': {
        en: 'Default Language',
        ar: 'اللغة الافتراضية'
      },
      'confirmAndContinue': {
        en: 'Confirm and Continue',
        ar: 'تأكيد ومتابعة'
      }
    };

    return translations[key]?.[modalLanguage] || key;
  };

  const fetchCountries = async () => {
    setIsLoading(true);
    try {
      // Fetch countries from API
      const response = await fetch('https://restcountries.com/v3.1/all?fields=name,cca2');
      const data = await response.json();
      
      // Filter to only include the countries we support
      const supportedCountryCodes = Object.keys(countryConfig);
      
      const formattedCountries: Country[] = data
        .filter((country: any) => supportedCountryCodes.includes(country.cca2))
        .map((country: any) => {
          const code = country.cca2;
          const config = countryConfig[code];
          
          return {
            name: country.name.common,
            code: code,
            currency: config.defaultCurrency,
          };
        })
        .sort((a: Country, b: Country) => a.name.localeCompare(b.name));
      
      setCountries(formattedCountries);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      // Fallback with essential countries
      const fallbackCountries: Country[] = Object.entries(countryConfig).map(([code, config]) => ({
        name: getCountryNameByCode(code),
        code,
        currency: config.defaultCurrency,
      })).sort((a, b) => a.name.localeCompare(b.name));
      
      setCountries(fallbackCountries);
    } finally {
      setIsLoading(false);
    }
  };

  const getCountryNameByCode = (code: string): string => {
    const countryNames: { [key: string]: string } = {
      'SA': 'Saudi Arabia',
      'OM': 'Oman',
      'BH': 'Bahrain',
      'AE': 'United Arab Emirates',
      'QA': 'Qatar',
      'KW': 'Kuwait',
      'BE': 'Belgium',
      'NL': 'Netherlands',
      'GB': 'United Kingdom',
      'ES': 'Spain',
      'IT': 'Italy',
      'AT': 'Austria',
      'FR': 'France',
      'US': 'United States',
      'CA': 'Canada',
      'IL': 'Israel',
    };
    return countryNames[code] || code;
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Set detected country and modal language when available
  useEffect(() => {
    if (detectedCountry?.countryCode && countries.length > 0) {
      const countryCode = detectedCountry.countryCode.toUpperCase();
      
      // Check if detected country is in our supported list
      const supportedCountry = countries.find(c => c.code === countryCode);
      
      if (supportedCountry) {
        setSelectedCountry(countryCode);
        // Set default currency for the country
        const defaultCurrency = getDefaultCurrencyForCountry(countryCode);
        setSelectedCurrency(defaultCurrency);
        
        // Set modal language based on detected country
        const detectedLanguage = getLanguageForCountry(countryCode);
        // console.log('Setting modal language:', { 
        //   countryCode, 
        //   detectedLanguage, 
        //   currentModalLanguage: modalLanguage 
        // });
        setModalLanguage(detectedLanguage);
      } else {
        // If detected country is not in our list, default to US
        // console.log('Detected country not in supported list:', countryCode);
        setSelectedCountry('US');
        setSelectedCurrency('USD');
        setModalLanguage('en');
      }
    } else if (countries.length > 0) {
      // Default to US if no detection
      setSelectedCountry('US');
      setSelectedCurrency('USD');
      setModalLanguage('en');
    }
  }, [detectedCountry, countries]);

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    
    // Reset currency based on new country
    const defaultCurrency = getDefaultCurrencyForCountry(countryCode);
    setSelectedCurrency(defaultCurrency);
    
    setShowResetWarning(true);
    
    // Change modal language when user selects a different country
    const newLanguage = getLanguageForCountry(countryCode);
    // console.log('Changing modal language to:', newLanguage);
    setModalLanguage(newLanguage);
  };

  const handleCurrencyChange = (currencyCode: string) => {
    setSelectedCurrency(currencyCode);
  };

  const handleConfirmSelection = () => {
    const language = getLanguageForCountry(selectedCountry);
    const currency = selectedCurrency;
    const countryName = countries.find(c => c.code === selectedCountry)?.name || selectedCountry;
    
    // console.log(`Confirming selection: Country=${countryName}, Language=${language}, Currency=${currency}, CountryCode=${selectedCountry}`);
    
    // Save to localStorage with both country code and name
    const choice = {
      locale: language,
      currency: currency,
      country: countryName,
      countryCode: selectedCountry
    };
    
    localStorage.setItem('user-locale-choice', JSON.stringify(choice));
    localStorage.setItem('user-currency-selection', currency);
    
    onConfirm(language, currency, countryName, selectedCountry);
  };

  const handleConfirmDetectedCountry = () => {
    const language = getLanguageForCountry(selectedCountry);
    const currency = selectedCurrency;
    const countryName = countries.find(c => c.code === selectedCountry)?.name || selectedCountry;
    
    // console.log(`Confirming detected country: Country=${countryName}, Language=${language}, Currency=${currency}, CountryCode=${selectedCountry}`);
    
    // Save to localStorage with both country code and name
    const choice = {
      locale: language,
      currency: currency,
      country: countryName,
      countryCode: selectedCountry
    };
    
    localStorage.setItem('user-locale-choice', JSON.stringify(choice));
    localStorage.setItem('user-currency-selection', currency);
    
    onConfirm(language, currency, countryName, selectedCountry);
  };

  // Get current configuration
  const currentCurrency = selectedCurrency;
  const currentLanguage = getLanguageForCountry(selectedCountry);
  const availableCurrenciesForCurrentCountry = getAvailableCurrenciesForCountry(selectedCountry);
  const showCurrencyDropdown = selectedCountry !== 'IL'; // Hide currency dropdown for Israel

  // Find detected country name from code
  const detectedCountryName = useMemo(() => {
    if (!detectedCountry?.countryCode || countries.length === 0) return detectedCountry?.country;
    const match = countries.find(
      (c) => c.code.toUpperCase() === detectedCountry.countryCode.toUpperCase()
    );
    return match ? match.name : detectedCountry.countryCode;
  }, [detectedCountry, countries]);

  if (!isOpen) return null;

  // console.log('Modal rendering with:', {
  //   modalLanguage,
  //   detectedCountry: detectedCountry?.countryCode,
  //   selectedCountry,
  //   detectedCountryName,
  //   selectedCurrency,
  //   availableCurrencies: availableCurrenciesForCurrentCountry,
  //   showCurrencyDropdown
  // });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] px-4 py-8"
      id='localeModal'
    >
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto locale-modal">
        <div className="px-8 pb-12 pt-6 text-center text-primary">
          <div className='flex items-center justify-end mb-6'>
            <svg
              version="1.1"
              id="Layer_1"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="30px"
              height="30px"
              viewBox="0 0 92 92"
              onClick={() => onConfirm('ar', 'ILS', 'Israel', 'IL')}
              className="cursor-pointer"
            >
              <path
                id="XMLID_732_"
                d="M70.7,64.3c1.8,1.8,1.8,4.6,0,6.4c-0.9,0.9-2,1.3-3.2,1.3
                  c-1.2,0-2.3-0.4-3.2-1.3L46,52.4L27.7,70.7
                  c-0.9,0.9-2,1.3-3.2,1.3s-2.3-0.4-3.2-1.3
                  c-1.8-1.8-1.8-4.6,0-6.4L39.6,46L21.3,27.7
                  c-1.8-1.8-1.8-4.6,0-6.4c1.8-1.8,4.6-1.8,6.4,0
                  L46,39.6l18.3-18.3c1.8-1.8,4.6-1.8,6.4,0
                  c1.8,1.8,1.8,4.6,0,6.4L52.4,46L70.7,64.3z"
              />
            </svg>
          </div>
          
          <h2 className="text-3xl font-semibold">{getTranslation('localeModalWelcomePartOne')}</h2>
          <h2 className="text-3xl font-semibold">{getTranslation('localeModalWelcomePartTwo')}</h2>

          {!showCountrySelection ? (
            <>
              <div className="my-2 md:my-4 p-2">
                <p className="text-md font-semibold text-blue-800 mb-2">
                  {getTranslation('detectionHeading')} {detectedCountryName || 'United States'}
                </p>
              </div>

              <button
                onClick={handleConfirmDetectedCountry}
                className="w-full bg-primary text-white text-md font-semibold px-4 py-2 rounded-xl mb-4 flex items-center justify-center gap-2"
                id='confirm-detected-country'
              >
                {/* {getTranslation('continueShopping')} */}
                <img
                  alt='flag'
                  className='w-8 h-8 rounded-full object-contain'
                  src={`https://flagsapi.com/${detectedCountry.countryCode}/shiny/64.png`}
                />
                <span>
                  {`${getTranslation('continueShoppingOne')} ${detectedCountry.countryCode} ${getTranslation('continueShoppingTwo')} ${selectedCurrency}`}
                </span>
              </button>

              <button
                onClick={() => setShowCountrySelection(true)}
                className="w-full bg-white border-primary border-[1px] text-primary text-md font-semibold px-4 py-2 rounded-xl mb-6"
              >
                {getTranslation('selectDifferentCountry')}
              </button>
            </>
          ) : (
            <>
              {/* Country Selection */}
              {/* {showResetWarning && (
                <div className="mb-4 p-3 bg-red-400 border border-yellow-200 rounded-lg mt-4">
                  <p className="text-sm text-yellow-800">
                    {getTranslation('countryChangeWarning')}
                  </p>
                </div>
              )} */}

              <div className={`mt-6 ${modalLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
                <label
                  className={`block text-sm font-medium text-gray-700 mb-1 ${
                    modalLanguage === 'ar' ? 'text-right' : 'text-left'
                  }`}
                >
                  {getTranslation('selectCountry')}
                </label>
                {isLoading ? (
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-100">
                    {getTranslation('loadingCountry')}
                  </div>
                ) : (
                  <CustomDropdown
                    options={countries.map(country => ({
                      value: country.code,
                      label: country.name,
                    }))}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    placeholder="Select a country"
                    className="w-full"
                    modalLanguage={modalLanguage}
                  />
                )}
              </div>

              {/* Currency Selection - Only show if not Israel */}
              {showCurrencyDropdown && selectedCountry && (
                <div className={`mt-4 ${modalLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
                  <label
                    className={`block text-sm font-medium text-gray-700 mb-1 ${
                      modalLanguage === 'ar' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {getTranslation('selectCurrency')}
                  </label>
                  {/* <select
                    value={selectedCurrency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableCurrenciesForCurrentCountry.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                    
                  </select> */}
                  <CustomDropdown
                      options={availableCurrenciesForCurrentCountry.map(currency => ({
                        value: currency.code,
                        label: currency.name,
                      }))}
                      value={selectedCurrency}
                      onChange={handleCurrencyChange}
                      placeholder="Select a currency"
                      className="w-full"
                      modalLanguage={modalLanguage}
                    />
                </div>
              )}

              {/* Selected country details */}
              {/* <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">
                  {countries.find(c => c.code === selectedCountry)?.name}
                </p>
                <p className="text-sm text-gray-600">
                  {getTranslation('defaultCurrency')}: {selectedCurrency} • {getTranslation('defaultLanguage')}: {currentLanguage === 'ar' ? 'العربية' : 'English'}
                  {!showCurrencyDropdown && (
                    <span className="block text-xs text-blue-500 mt-1">
                      Note: Israeli users use ILS currency only
                    </span>
                  )}
                </p>
              </div> */}

              <button
                onClick={handleConfirmSelection}
                className="w-full bg-primary text-white text-md font-semibold p-4 rounded-lg mt-6"
              >
                {getTranslation('confirmAndContinue')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocaleConfirmationModal;